import { Plus } from "lucide-react";
import React from "react";
import { useParams } from "react-router-dom";
import { Dimmer, Loader } from "semantic-ui-react";
import CommonSplitContainer from "../../../components/commonSplitContainer/CommonSplitContainer";
import { Button } from "../../../components/ui/common/button";
import RecipeSimulatorDetailsView from "../RecipeResultDetailsView";
import ChangeHistory from "./ChangeHistory";
import ManageRecipeVariantModals from "./components/ManageRecipeVariantModals";
import RecipeVariantTitleHeader from "./components/RecipeVariantTitleHeader";
import { useManageRecipeVariant } from "./hooks/useManageRecipeVariant";
import ProcessAnalysis from "./ProcessAnalysis";
import "./recipeVariant.scss";
import RecipeVariantTable from "./recipeVariantTable/RecipeVariantTable";

const ManageRecipeVariant: React.FC = () => {
  const { recipeId }: any = useParams();
  const vm = useManageRecipeVariant(recipeId);

  if (vm.isLoading) {
    return (
      <div className="tw-rounded-lg tw-bg-white">
        <Dimmer active>
          <Loader content="Loading" />
        </Dimmer>
      </div>
    );
  }

  return (
    <CommonSplitContainer
      overflowX={"hidden"}
      scrollbarWidth={"none"}
      currentHeight={0.99}
      containerClassName="tw-overflow-y-auto tw-overflow-x-hidden tw-rounded-lg tw-bg-white [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:tw-hidden"
      topChild={
        <>
          <RecipeVariantTitleHeader
            recipeId={recipeId ?? ""}
            recipeData={vm.recipeData}
            isViewOnly={vm.isViewOnly}
            onRecipeUpdated={vm.recipeDataRefetch}
          />
          {vm.isViewOnly && (
            <div className="tw-mx-1 tw-my-4 tw-rounded-md tw-border tw-border-primary/20 tw-bg-primary/5 tw-px-4 tw-py-2 tw-text-sm tw-text-primary">
              View only — you cannot edit this recipe.
            </div>
          )}
          <div className="recipe-create-grid tw-mt-1">
            {!vm.isViewOnly && (
              <div className="tw-mt-6 tw-mb-3 tw-flex tw-justify-end tw-gap-2">
                <Button
                  type="button"
                  size="lg"
                  className="tw-h-11 tw-px-7 tw-text-base tw-gap-2"
                  onClick={() => vm.onButtonAction(0)}
                >
                  <Plus className="tw-h-5 tw-w-5" />
                  New Ingredient
                </Button>
                {!vm.isNewRecipe && (
                  <Button
                    type="button"
                    size="lg"
                    className="tw-h-11 tw-px-7 tw-text-base tw-gap-2"
                    onClick={() => vm.onButtonAction(1)}
                  >
                    <Plus className="tw-h-5 tw-w-5" />
                    New Variation
                  </Button>
                )}
              </div>
            )}
            <RecipeVariantTable
              recipe={{ originalRecipe: { ingredients: vm.ingredientNames } }}
              data={vm.recipeData ?? null}
              control={vm.control}
              errors={vm.errors}
              totals={vm.totals}
              fixedColumns={vm.fixedColumnsWithRealTimeUpdates}
              dynamicColumns={vm.dynamicColumnsWithRealTimeUpdates}
              variants={vm.variants}
              addDeleteColumn={!vm.isViewOnly}
              setProcessingStepVisibleModal={vm.setProcessingStepVisibleModal}
              setSelectedProcess={vm.setSelectedProcess}
              setProcessingStepViewModal={vm.setProcessingStepViewModal}
              setIsOriginal={vm.setIsOriginal}
              setDeleteIngredient={vm.setDeleteIngredient}
              setVariantId={vm.setVariantId}
              setVisibleUpdateModal={vm.setVisibleUpdateModal}
              onRemoveVariant={vm.isViewOnly ? undefined : vm.handleRemoveVariant}
              updateIngredientPercentage={vm.debouncedUpdateIngredientPercentage}
              recipeId={recipeId ?? ""}
              onVariantNameUpdated={vm.recipeDataRefetch}
              onOpenEditVariation={variantId => {
                vm.setVariantId(variantId);
                vm.setVisibleUpdateModal(true);
              }}
              readOnly={vm.isViewOnly}
            />
            {!vm.isNewRecipe && !vm.isViewOnly ? (
              <div className="tw-mt-4 tw-flex tw-justify-end">
                <Button
                  type="submit"
                  size="lg"
                  className="tw-h-11 tw-px-7 tw-text-base"
                  onClick={vm.handleSubmit(vm.onSaveAndCalculate)}
                >
                  Calculate Recipe
                </Button>
              </div>
            ) : null}
          </div>

          <div id="recipe-results" className="mt-1">
            {vm.recipeResults && (
              <>
                <RecipeSimulatorDetailsView
                  recipeResults={vm.recipeResults}
                  originalRecipeData={vm.recipeData ?? null}
                  productSize={vm.productSize}
                  setProductSize={vm.setProductSize}
                  selectedVariants={vm.selectedVariants}
                  setSelectedVariants={vm.setSelectedVariants}
                  readOnly={vm.isViewOnly}
                />
                <ProcessAnalysis
                  recipeResults={vm.recipeResults ? true : false}
                  onScrollToGrid={vm.handleToggleScroll}
                  downloadPdf={vm.downloadPdf}
                  hasAnyUpdates={vm.hasAnyUpdates}
                  readOnly={vm.isViewOnly}
                  recipeId={recipeId}
                />
              </>
            )}
          </div>
          {recipeId ? (
            <ManageRecipeVariantModals
              recipeId={recipeId}
              recipeData={vm.recipeData}
              recipeDataRefetch={vm.recipeDataRefetch}
              genericProcess={vm.genericProcess}
              usedColors={vm.usedColors}
              isViewOnly={vm.isViewOnly}
              isOriginal={vm.isOriginal}
              variantId={vm.variantId}
              selectedProcess={vm.selectedProcess}
              deleteIngredient={vm.deleteIngredient}
              deleteVariant={vm.deleteVariant}
              visibleModal={vm.visibleModal}
              visibleUpdateModal={vm.visibleUpdateModal}
              visibleRecipeUpdateModal={vm.visibleRecipeUpdateModal}
              ingredientVisibleModal={vm.ingredientVisibleModal}
              processingStepVisibleModal={vm.processingStepVisibleModal}
              processingStepViewModal={vm.processingStepViewModal}
              processingStepEditModal={vm.processingStepEditModal}
              setVisibleModal={vm.setVisibleModal}
              setVisibleUpdateModal={vm.setVisibleUpdateModal}
              setVisibleRecipeUpdateModal={vm.setVisibleRecipeUpdateModal}
              setIngredientVisibleModal={vm.setIngredientVisibleModal}
              setProcessingStepVisibleModal={vm.setProcessingStepVisibleModal}
              setProcessingStepViewModal={vm.setProcessingStepViewModal}
              setProcessingStepEditModal={vm.setProcessingStepEditModal}
              setVariantId={vm.setVariantId}
              setSelectedProcess={vm.setSelectedProcess}
              setDeleteIngredient={vm.setDeleteIngredient}
              setDeleteVariant={vm.setDeleteVariant}
              handleDeleteIngredient={vm.handleDeleteIngredient}
              deleteVariants={vm.deleteVariants}
            />
          ) : null}
        </>
      }
      bottomChild={<></>}
      scrollToBottomTrigger={vm.scrollToBottom}
      scrollToTopTrigger={vm.scrollToTop}
      onScrollPositionChange={vm.handleScrollPositionChange}
    />
  );
};

export default ManageRecipeVariant;
